import iso from "iso-3166-1";

import {
  UNKNOWN_CITY,
  UNKNOWN_COUNTRY,
  countryFlag,
  normalizeCity,
} from "@/pages/tina/runs/components/site-picker/utils";
import type { IIdentity } from "@/providers/auth-provider";
import type { ScheduledCoverageRow, TodayCoverageRow } from "@/types/tina";

import { curatorColorAt } from "../world-domination/lib";

export interface CuratorRef {
  uid: string;
  displayName: string;
  photoUrl: string | null;
  color: string;
}
export interface SiteCoverage {
  name: string;
  curators: CuratorRef[];
}
export interface CityCoverage {
  city: string;
  siteCount: number;
  curators: CuratorRef[];
  sites: SiteCoverage[];
}
export interface CountryCoverage {
  countryCode: string;
  countryName: string;
  flag: string;
  siteCount: number;
  cityCount: number;
  curators: CuratorRef[];
  cities: CityCoverage[];
}
export interface CoverageData {
  countries: CountryCoverage[];
  totalCountries: number;
  totalSites: number;
  totalCurators: number;
}

// Order curators by how many distinct sites they ran (desc), tie-broken by name,
// then assign each a stable golden-angle colour — same palette family as the
// world-domination map. Colour is card-local (not tied to that map's ranking).
function buildCuratorRefs(
  rows: TodayCoverageRow[],
  members: IIdentity[],
): Map<string, CuratorRef> {
  const byId = new Map(members.map((m) => [m.id, m]));
  const sitesPerUid = new Map<string, Set<string>>();
  for (const r of rows) {
    let set = sitesPerUid.get(r.triggered_by);
    if (!set) {
      set = new Set();
      sitesPerUid.set(r.triggered_by, set);
    }
    set.add(r.site);
  }
  const uids = [...sitesPerUid.keys()].sort((a, b) => {
    const d = (sitesPerUid.get(b)?.size ?? 0) - (sitesPerUid.get(a)?.size ?? 0);
    if (d !== 0) return d;
    const na = byId.get(a)?.displayName ?? a;
    const nb = byId.get(b)?.displayName ?? b;
    return na.localeCompare(nb);
  });
  const refs = new Map<string, CuratorRef>();
  uids.forEach((uid, i) => {
    const m = byId.get(uid);
    refs.set(uid, {
      uid,
      displayName: m?.displayName ?? m?.email ?? uid,
      photoUrl: m?.photoUrl ?? null,
      color: curatorColorAt(i),
    });
  });
  return refs;
}

// Distinct curator refs in global (card) order.
function orderedCurators(
  uids: Set<string>,
  refs: Map<string, CuratorRef>,
): CuratorRef[] {
  return [...refs.values()].filter((r) => uids.has(r.uid));
}

function countryName(cc: string): string {
  return (
    iso.whereAlpha2(cc)?.country ?? (cc === UNKNOWN_COUNTRY ? "Unknown" : cc)
  );
}

export function buildCoverage(
  rows: TodayCoverageRow[],
  members: IIdentity[],
): CoverageData {
  const refs = buildCuratorRefs(rows, members);

  // country → city → site → set<uid>
  const countries = new Map<string, Map<string, Map<string, Set<string>>>>();
  for (const r of rows) {
    const cc = (r.country_code ?? UNKNOWN_COUNTRY).toUpperCase();
    const city = normalizeCity(r.city);
    let cities = countries.get(cc);
    if (!cities) {
      cities = new Map();
      countries.set(cc, cities);
    }
    let sites = cities.get(city);
    if (!sites) {
      sites = new Map();
      cities.set(city, sites);
    }
    let uids = sites.get(r.site);
    if (!uids) {
      uids = new Set();
      sites.set(r.site, uids);
    }
    uids.add(r.triggered_by);
  }

  const cityLast = (c: string) => (c === UNKNOWN_CITY ? 1 : 0);
  const countryLast = (c: string) => (c === UNKNOWN_COUNTRY ? 1 : 0);

  const countryList: CountryCoverage[] = [...countries.entries()].map(
    ([cc, citiesMap]) => {
      const countryUids = new Set<string>();
      const cities: CityCoverage[] = [...citiesMap.entries()].map(
        ([city, sitesMap]) => {
          const cityUids = new Set<string>();
          const sites: SiteCoverage[] = [...sitesMap.entries()]
            .map(([name, uids]) => {
              uids.forEach((u) => {
                cityUids.add(u);
                countryUids.add(u);
              });
              return { name, curators: orderedCurators(uids, refs) };
            })
            .sort((a, b) => a.name.localeCompare(b.name));
          return {
            city,
            siteCount: sites.length,
            curators: orderedCurators(cityUids, refs),
            sites,
          };
        },
      );
      cities.sort(
        (a, b) =>
          cityLast(a.city) - cityLast(b.city) || b.siteCount - a.siteCount,
      );
      const siteCount = cities.reduce((n, c) => n + c.siteCount, 0);
      return {
        countryCode: cc,
        countryName: countryName(cc),
        flag: countryFlag(cc),
        siteCount,
        cityCount: cities.length,
        curators: orderedCurators(countryUids, refs),
        cities,
      };
    },
  );
  countryList.sort(
    (a, b) =>
      countryLast(a.countryCode) - countryLast(b.countryCode) ||
      b.siteCount - a.siteCount,
  );

  const allSites = new Set<string>();
  rows.forEach((r) => allSites.add(r.site));

  return {
    countries: countryList,
    totalCountries: countryList.length,
    totalSites: allSites.size,
    totalCurators: refs.size,
  };
}

// The copied list is split into one block per vertical, in this order. A site
// whose vertical is not one of these lands in a last "Other" block rather than
// disappearing.
const VERTICALS: { contentType: string; label: string }[] = [
  { contentType: "music_event", label: "Music" },
  { contentType: "cinema_screening", label: "Cinema" },
  { contentType: "theater_performance", label: "Theater" },
  { contentType: "sports_match", label: "Sport" },
];
const OTHER_VERTICAL = "Other";

// A country-wide site (Europe Sports, the Greek cinema guides, the Instagram
// groups) has `city = "All"`; "Greece - All" reads like a city called All.
const ALL_CITIES_SITE_CITY = "all";

function verticalLabel(contentType: string): string {
  return (
    VERTICALS.find((v) => v.contentType === contentType)?.label ??
    OTHER_VERTICAL
  );
}

// One "Country - City" line per city the site groups run on their schedule, for
// pasting into a sheet. It lists what is scheduled, not what ran today: the team
// wants to see what Tina covers on its own, and a manual run or a weekday
// without a cinema group would make "today" say something else. Lines are grouped
// under a heading per vertical (a city with music and cinema sites shows up under
// both) and sorted by name within a vertical so each block reads like an index.
// Empty verticals and unspecified cities are left out.
// A country-wide site is listed as "Country - All cities".
export function scheduledCitiesText(rows: ScheduledCoverageRow[]): string {
  const linesByVertical = new Map<string, Set<string>>();
  for (const r of rows) {
    const city = normalizeCity(r.city);
    if (city === UNKNOWN_CITY) continue;
    const cc = (r.country_code ?? UNKNOWN_COUNTRY).toUpperCase();
    const label = verticalLabel(r.content_type);
    let lines = linesByVertical.get(label);
    if (!lines) {
      lines = new Set();
      linesByVertical.set(label, lines);
    }
    const place =
      city.toLowerCase() === ALL_CITIES_SITE_CITY ? "All cities" : city;
    lines.add(`${countryName(cc)} - ${place}`);
  }
  const order = [...VERTICALS.map((v) => v.label), OTHER_VERTICAL];
  return order
    .filter((label) => linesByVertical.has(label))
    .map((label) => {
      const lines = [...linesByVertical.get(label)!].sort((a, b) =>
        a.localeCompare(b),
      );
      return [label, ...lines].join("\n");
    })
    .join("\n\n");
}

// The same schedule as `scheduledCitiesText`, but one line per site with the
// Instagram handle it is scraped under. The city list answers "where does Tina
// run"; this answers "which accounts does Tina run", so the site names stay.
// Shape: one block per vertical, a `Country - City` heading per city, then its
// sites indented under it. Same drops as the city list: an unspecified city is
// left out, and a country-wide site is listed under "All cities".
export function scheduledSitesText(rows: ScheduledCoverageRow[]): string {
  const sitesByVertical = new Map<string, Map<string, Set<string>>>();
  for (const r of rows) {
    const city = normalizeCity(r.city);
    if (city === UNKNOWN_CITY) continue;
    const cc = (r.country_code ?? UNKNOWN_COUNTRY).toUpperCase();
    const place =
      city.toLowerCase() === ALL_CITIES_SITE_CITY ? "All cities" : city;
    const label = verticalLabel(r.content_type);
    let cities = sitesByVertical.get(label);
    if (!cities) {
      cities = new Map();
      sitesByVertical.set(label, cities);
    }
    const heading = `${countryName(cc)} - ${place}`;
    let sites = cities.get(heading);
    if (!sites) {
      sites = new Set();
      cities.set(heading, sites);
    }
    const handle = r.instagram_handle ? ` (@${r.instagram_handle})` : "";
    sites.add(`${r.site}${handle}`);
  }
  const order = [...VERTICALS.map((v) => v.label), OTHER_VERTICAL];
  return order
    .filter((label) => sitesByVertical.has(label))
    .map((label) => {
      const blocks = [...sitesByVertical.get(label)!]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([heading, sites]) =>
          [
            heading,
            ...[...sites]
              .sort((a, b) => a.localeCompare(b))
              .map((s) => `  ${s}`),
          ].join("\n"),
        );
      return [label, ...blocks].join("\n");
    })
    .join("\n\n");
}
